import {
  Card,
  CardBody,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
} from "@chakra-ui/react";
import { CreditFacilityEventsTable } from "features/credit-facility/CreditFacilityEventsTable";
import { CreditFacilityPrincipalUsesTable } from "features/credit-facility/CreditFacilityPrincipalUsesTable";
import {
  useCreditFacilityRetrieveSuspense,
  useCreditFacilitySummaryRetrieveSuspense,
} from "gen/endpoints/credit-facility/credit-facility";
import { CreditFacilityStateMachineFieldsEnum } from "gen/model/creditFacilityStateMachineFieldsEnum";
import React from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { formatMoney } from "shared/utils/MoneyUtils";

export const CreditFacilityPrincipalTab: React.FC = () => {
  const { t } = useTranslation(["common", "creditFacility"]);

  const { fundFamilyId, creditFacilityId } = useRequiredStringParams();

  const { data: creditFacility } = useCreditFacilityRetrieveSuspense(
    fundFamilyId,
    creditFacilityId,
    {
      query: {
        refetchOnMount: "always",
      },
    },
  );
  const { data: summaryData } = useCreditFacilitySummaryRetrieveSuspense(
    fundFamilyId,
    creditFacilityId,
  );

  if (creditFacility.state === CreditFacilityStateMachineFieldsEnum.draft) {
    return (
      <Navigate
        replace={true}
        to={
          rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId })
            .setup({})
            .creditFacilities({})
            .creditFacility({ creditFacilityId }).$
        }
      />
    );
  }

  return (
    <PageTabBody isFullHeight={true}>
      <Card variant="stat" w="100%" mb="24px">
        <CardBody>
          <StatGroup p="24px !important">
            <Stat>
              <StatNumber>
                {formatMoney(summaryData.commitment_amount)}
              </StatNumber>
              <StatLabel>
                {t("creditFacility:stats.commitmentAmount")}
              </StatLabel>
            </Stat>
            <Stat>
              <StatNumber>
                {formatMoney(summaryData.outstanding_amount)}
              </StatNumber>
              <StatLabel>
                {t("creditFacility:stats.outstandingPrincipalAmount")}
              </StatLabel>
            </Stat>
            <Stat>
              <StatNumber>
                {formatMoney(summaryData.borrowing_base_amount)}
              </StatNumber>
              <StatLabel>
                {t("creditFacility:stats.borrowingBaseAmount")}
              </StatLabel>
            </Stat>
            <Stat>
              <StatNumber>
                {formatMoney(summaryData.unfunded_amount)}
              </StatNumber>
              <StatLabel>
                {t("creditFacility:stats.unfundedFacilityCommitmentAmount")}
              </StatLabel>
            </Stat>
            <Stat>
              <StatNumber>
                {formatMoney(summaryData.available_amount)}
              </StatNumber>
              <StatLabel>
                {t("creditFacility:stats.availableBorrowingAmount")}
              </StatLabel>
            </Stat>
          </StatGroup>
        </CardBody>
      </Card>
      <CreditFacilityEventsTable />
      <CreditFacilityPrincipalUsesTable
        title={t("creditFacility:summary.principalTracking")}
      />
    </PageTabBody>
  );
};
